import React from 'react'
import { CARD_LAYOUT_FIELD } from "../models/shared/CardLayout";
import { Deal } from "../models/shared/Deal";
import { Offer } from "../models/shared/Offer";
import { SearchResultDeal } from "../models/shared/SearchResults";
import { formatDate } from "./date";
import { getLatestOffer } from "./logic";
import { LANGUAGE } from "./shared/constants";
import { calculateLineItemsTotal, formatPrice } from "./shared/priceCalculator";
import { textApplyVariables, TextContext } from '../context/TextContext';
import OfferState from '../components/OfferState';
import { AuditDealRecord } from '../models/shared/DealAuditRecord';
import { Organization } from '../models/shared/Organization';

const COLOR_ICON_PRIMARY = '#FFFFFF';

interface MapPinOptions {
    type?: 'tree' | 'home'
    backgroundColor?: string
}
export const generateMapPin = (PinElement: any, options: MapPinOptions = {}) => {
    const icon = document.createElement('div');
    const isHome = options.type === 'home';

    icon.innerHTML = `<i class="bi ${isHome ? 'bi-car-front-fill' : 'bi-tree-fill'}" style="color: ${COLOR_ICON_PRIMARY};"></i>`;

    const backgroundColor = options.backgroundColor || (isHome ? '#1E90FF' : '#32CD32'); // Dodger Blue for home, Lime Green for tree

    return new PinElement({
        glyph: icon,
        background: backgroundColor,
        borderColor: isHome ? '#00008B' : '#006400', // Dark Blue for home, Dark Green for tree
    });
}

export const renderDealContent = (field: CARD_LAYOUT_FIELD, deal: Deal, offers: Offer[], text: TextContext) => {
    if (field === CARD_LAYOUT_FIELD.NAME) {
        return deal.name;
    }

    if (field === CARD_LAYOUT_FIELD.UPDATED_AT) {
        return deal.updatedAt ? `${text.lastUpdated} ${formatDate(deal.updatedAt)}` : ''
    }

    if (field === CARD_LAYOUT_FIELD.ADDRESS) {
        let address = deal.place?.name || '';
        if (deal.placeDistance) {
            const distanceKm = Math.round((deal.placeDistance.distanceMeters || 0) / 1000);
            address += ` (${distanceKm} km)`;
        }
        return address;
    }

    if (field === CARD_LAYOUT_FIELD.OFFER) {
        const latestOffer = getLatestOffer(offers);
        if (!latestOffer) {
            return <span>{text.noOffer}</span>;
        }
        const sums = calculateLineItemsTotal(latestOffer.lineItems || []);

        return <span className='center gap-1'>
            {formatPrice(sums.totalWithVat)}
            <OfferState offer={latestOffer} />
        </span>;
    }

    return '';
}

export const renderSearchDealContent = (field: CARD_LAYOUT_FIELD, searchHit: SearchResultDeal, text: any) => {
    if (field === CARD_LAYOUT_FIELD.NAME) {
        return searchHit.name;
    }

    if (field === CARD_LAYOUT_FIELD.ADDRESS) {
        return searchHit.address || '';
    }

    return '';
}

export const zeroPad = (num: number, places: number = 2) => String(num).padStart(places, '0');

export const LANGUAGE_OPTIONS = [
    { value: LANGUAGE.EN, label: 'English' },
    { value: LANGUAGE.SV, label: 'Svenska' },
    { value: LANGUAGE.CS, label: 'Čeština' },
];

export const getBrowserLanguage = (): LANGUAGE => {
    const languageFromBrowser = navigator?.language;
    if (!languageFromBrowser) return LANGUAGE.EN;

    const browserLang = languageFromBrowser.split('-')[0].toLowerCase();

    switch (browserLang) {
        case 'sv': return LANGUAGE.SV;
        case 'cs': return LANGUAGE.CS;
        default: return LANGUAGE.EN;
    }
};

export function renderAuditMessage(record: AuditDealRecord, organization: Organization, textCtx: any): string {
    if (record.type === 'stepChange') {
        const orgSteps = organization.dealSteps || [];
        const oldStep = orgSteps.find(step => step.id === record.extras.oldStepId)?.name || '';
        const newStep = orgSteps.find(step => step.id === record.extras.newStepId)?.name || '';
        let message = textApplyVariables(textCtx.createdIn, { step: newStep });
        if (oldStep) {
            message = textApplyVariables(textCtx.movedFromTo, { from: oldStep, to: newStep });
        }
        return message;
    } else {
        return '';
    }
}